/**
 * Which parts are drawn, as a pure function of what the user has asked for.
 *
 * The resolver is the single place that decides visibility. Services hold the
 * intent (which systems are on, what is isolated, what is hidden), this turns
 * that into a mask, and the render bridge applies the difference. Nothing here
 * touches the engine.
 *
 * The mask is a `Uint8Array` indexed by `Catalog.slotOf`, so a whole-body
 * resolve is one linear pass over 2,229 entries with no allocation per part,
 * and the bridge only calls `setVisible` where the mask actually changed. That
 * matters: every visibility change marks a batch dirty and costs a rescan.
 */
import type { Catalog } from "../model.js";

export interface VisibilityState {
  /** System ids that are switched on. */
  systems: ReadonlySet<string>;
  /**
   * When set, only these parts draw, whatever their system says. This is
   * "show me just the heart": isolation, not a system toggle.
   */
  isolated: ReadonlySet<string> | null;
  /** Parts the user has hidden individually; applied after everything else. */
  hidden: ReadonlySet<string>;
}

export function emptyVisibility(): VisibilityState {
  return { systems: new Set(), isolated: null, hidden: new Set() };
}

/** The state that shows a bundle's default systems and nothing else. */
export function defaultVisibility(catalog: Catalog): VisibilityState {
  return {
    systems: new Set(catalog.systems.filter((s) => s.defaultVisible).map((s) => s.id)),
    isolated: null,
    hidden: new Set(),
  };
}

/**
 * Resolve to a mask: 1 where the part draws. `into` is reused between frames so
 * a resolve allocates nothing once the client is running.
 */
export function resolveVisibility(catalog: Catalog, state: VisibilityState, into?: Uint8Array): Uint8Array {
  const mask = into && into.length === catalog.parts.length ? into : new Uint8Array(catalog.parts.length);
  const { systems, isolated, hidden } = state;
  for (let i = 0; i < catalog.parts.length; i++) {
    const part = catalog.parts[i]!;
    let on = isolated ? isolated.has(part.id) : systems.has(part.system);
    if (on && hidden.has(part.id)) on = 0 as unknown as boolean;
    mask[i] = on ? 1 : 0;
  }
  return mask;
}

/**
 * Slots whose visibility differs. The bridge walks this rather than all 2,229
 * parts, so toggling one system touches only that system's slots.
 */
export function visibilityDiff(previous: Uint8Array | null, next: Uint8Array, into?: number[]): number[] {
  const out = into ?? [];
  out.length = 0;
  if (previous === null || previous.length !== next.length) {
    for (let i = 0; i < next.length; i++) out.push(i);
    return out;
  }
  for (let i = 0; i < next.length; i++) if (previous[i] !== next[i]) out.push(i);
  return out;
}

/** Triangles the mask draws, for the budget readout. */
export function trianglesVisible(catalog: Catalog, mask: Uint8Array): number {
  let total = 0;
  for (let i = 0; i < mask.length; i++) if (mask[i] === 1) total += catalog.parts[i]!.tris;
  return total;
}

// --- intent helpers, all returning a new state ------------------------------

export function toggleSystem(state: VisibilityState, systemId: string, on?: boolean): VisibilityState {
  const systems = new Set(state.systems);
  const next = on ?? !systems.has(systemId);
  if (next) systems.add(systemId);
  else systems.delete(systemId);
  return { ...state, systems };
}

export function setSystems(state: VisibilityState, systemIds: Iterable<string>): VisibilityState {
  return { ...state, systems: new Set(systemIds) };
}

/** Show only these parts. Passing an empty set clears isolation rather than hiding everything. */
export function isolate(state: VisibilityState, parts: Iterable<string>): VisibilityState {
  const set = new Set(parts);
  return { ...state, isolated: set.size > 0 ? set : null };
}

export function clearIsolation(state: VisibilityState): VisibilityState {
  return { ...state, isolated: null };
}

export function hidePart(state: VisibilityState, partId: string, hidden = true): VisibilityState {
  const set = new Set(state.hidden);
  if (hidden) set.add(partId);
  else set.delete(partId);
  return { ...state, hidden: set };
}

/**
 * Named layer states, which is what the side-by-side comparison shows: the same
 * body three times, each resolved through a different one of these.
 */
export type LayerPresetId = "full" | "bones-and-muscles" | "bones" | "organs" | "vessels" | "nerves" | "no-skin";

export interface LayerPreset {
  id: LayerPresetId;
  name: string;
  /** The systems to show, or `"all"` for every system the bundle carries. */
  systems: readonly string[] | "all";
  /** Systems left out of an `"all"`; the skin is the only one so far. */
  except?: readonly string[];
}

/**
 * The skin is the one system the atlas ships hidden (`defaultVisible: false`,
 * drawn translucent), because it covers everything else. "Full body" puts it
 * on; "Without skin" is the atlas's own default set. The order is the order
 * of the buttons, on the desk and in the Systems window (which has one slot
 * per preset: `SYSTEM_SLOTS`'s sibling constant in `ui/windows.ts`).
 */
export const LAYER_PRESETS: readonly LayerPreset[] = [
  { id: "full", name: "Full body", systems: "all" },
  { id: "bones-and-muscles", name: "Bones and muscles", systems: ["skeletal", "muscular"] },
  { id: "bones", name: "Bones", systems: ["skeletal"] },
  { id: "organs", name: "Organs", systems: ["cardiac", "respiratory", "digestive", "urinary", "endocrine", "reproductive"] },
  { id: "vessels", name: "Blood vessels", systems: ["arterial", "venous"] },
  { id: "nerves", name: "Nerves and senses", systems: ["nervous", "sensory"] },
  { id: "no-skin", name: "Without skin", systems: "all", except: ["integumentary"] },
];

export function presetById(id: LayerPresetId): LayerPreset | undefined {
  return LAYER_PRESETS.find((p) => p.id === id);
}

/**
 * Apply a preset. Systems the bundle does not carry are dropped, so a preset
 * naming an absent system yields that preset's remaining systems rather than
 * an empty body.
 */
export function applyPreset(catalog: Catalog, state: VisibilityState, preset: LayerPreset): VisibilityState {
  const except = new Set(preset.except ?? []);
  const wanted = preset.systems === "all" ? catalog.systems.map((s) => s.id).filter((id) => !except.has(id)) : preset.systems.filter((id) => catalog.systemById.has(id));
  return { systems: new Set(wanted), isolated: null, hidden: new Set() };
}
